import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Camera, Check, Eye, EyeOff, Globe2, MessageCircle } from "lucide-react";
import { apiErrorMessage } from "@/lib/api";
import { useLogin } from "@/lib/queries";
import { Logo } from "@/components/Logo";
import { Notice, Spinner } from "@/components/ui";

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await login.mutateAsync({ email, password });
      navigate("/", { replace: true });
    } catch (err) {
      setError(apiErrorMessage(err, t("errors.generic")));
    }
  }

  return (
    <AuthShell title={t("auth.loginTitle")} subtitle={t("auth.loginSubtitle")}>
      <form onSubmit={submit} className="space-y-4">
        <Field
          label={t("auth.email")}
          type="email"
          value={email}
          onChange={setEmail}
          autoComplete="email"
          required
        />
        <Field
          label={t("auth.password")}
          type="password"
          value={password}
          onChange={setPassword}
          autoComplete="current-password"
          required
        />
        <div className="-mt-2 text-right">
          <Link
            to="/forgot-password"
            className="text-sm font-medium text-primary-700 hover:underline"
          >
            {t("auth.forgotPassword")}
          </Link>
        </div>
        {error ? <ErrorBox>{error}</ErrorBox> : null}
        <button type="submit" disabled={login.isPending} className="btn-primary w-full">
          {login.isPending ? <Spinner /> : null}
          {login.isPending ? t("auth.signingIn") : t("auth.signIn")}
        </button>
      </form>
      <p className="mt-6 text-center text-sm text-muted">
        {t("auth.noAccount")}{" "}
        <Link to="/register" className="font-medium text-primary-700 hover:underline">
          {t("auth.signUp")}
        </Link>
      </p>
    </AuthShell>
  );
}

// =============================================================================
// Split in two: the form on one side, the reason to use the product on the
// other.
//
// The wordmark sits with the form. It is a true alpha cutout now, so it reads
// on white as well as it does on the dark half, and putting it above the
// fields is where a returning user looks to confirm they are in the right
// place. The marketing half collapses entirely below `lg` — on a phone the
// only thing that matters is signing in.
//
// Three lines, not a brochure: a headline, one sentence, three four-word
// proofs. Anyone on this page has already decided to sign in.
// =============================================================================

export function AuthShell({
  title,
  subtitle,
  children,
}: {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      {/* ── Form half ── */}
      <div className="flex items-center justify-center bg-white px-5 py-10 sm:px-10">
        <div className="w-full max-w-sm animate-fade-up">
          <Logo className="mb-9 h-7" alt={t("appName")} />

          <h1 className="text-2xl font-semibold tracking-tight text-ink">{title}</h1>
          {subtitle ? (
            <p className="mb-7 mt-1.5 text-sm text-muted">{subtitle}</p>
          ) : (
            <div className="mb-7" />
          )}

          {children}
        </div>
      </div>

      {/* ── Marketing half ── */}
      <div className="relative hidden overflow-hidden bg-night-900 lg:flex lg:flex-col lg:justify-center">
        {/* A single soft light source behind the copy; cheap depth, no image. */}
        <div
          className="pointer-events-none absolute -top-24 end-[-10%] h-[28rem] w-[28rem] rounded-full bg-primary-500/20 blur-3xl"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute bottom-[-15%] start-[-10%] h-96 w-96 rounded-full bg-primary-700/25 blur-3xl"
          aria-hidden
        />

        <div className="relative z-10 px-12 xl:px-16">
          <h2 className="max-w-md text-[2.1rem] font-semibold leading-[1.15] tracking-tight text-white">
            {t("marketing.headline")}
          </h2>
          <p className="mt-4 max-w-sm text-[15px] leading-relaxed text-white/60">
            {t("marketing.sub")}
          </p>

          <ul className="mt-9 space-y-3">
            {[
              { icon: <Camera size={15} />, key: "photo" },
              { icon: <MessageCircle size={15} />, key: "whatsapp" },
              { icon: <Globe2 size={15} />, key: "reach" },
            ].map((point) => (
              <li key={point.key} className="flex items-center gap-3 text-sm text-white/80">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/10 text-primary-200">
                  {point.icon}
                </span>
                {t(`marketing.points.${point.key}.title`)}
              </li>
            ))}
          </ul>

          <p className="mt-9 flex items-center gap-2 text-[13px] text-white/40">
            <Check size={14} className="text-primary-300" />
            {t("marketing.footnote")}
          </p>
        </div>
      </div>
    </div>
  );
}

/**
 * One field, and for a password one that can be revealed.
 *
 * The toggle lives here rather than on each form so sign-in, sign-up and the
 * reset screen all get it from one place — and so none of them can be the one
 * that forgets. On a phone, where a typo in an invisible field is the usual
 * reason a correct password gets rejected, it is the difference between
 * signing in and giving up.
 *
 * It is a `button` with type="button": inside a form, a bare <button> submits,
 * so revealing the password would try to sign in with whatever was typed so
 * far. It is also removed from the tab order — someone tabbing from password
 * to "Sign in" does not want a detour — and labelled for screen readers,
 * which otherwise announce it as an unnamed button.
 */
export function Field({
  label,
  type = "text",
  value,
  onChange,
  hint,
  ...rest
}: {
  label: string;
  type?: string;
  value: string;
  onChange: (v: string) => void;
  hint?: string;
} & Omit<React.InputHTMLAttributes<HTMLInputElement>, "value" | "onChange" | "type">) {
  const { t } = useTranslation();
  const [revealed, setRevealed] = useState(false);
  const isPassword = type === "password";

  return (
    <label className="block">
      <span className="label">{label}</span>
      <div className="relative">
        <input
          type={isPassword && revealed ? "text" : type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          className={`input ${isPassword ? "pr-11" : ""}`}
          {...rest}
        />
        {isPassword ? (
          <button
            type="button"
            tabIndex={-1}
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? t("auth.hidePassword") : t("auth.showPassword")}
            title={revealed ? t("auth.hidePassword") : t("auth.showPassword")}
            className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-muted hover:text-ink"
          >
            {revealed ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        ) : null}
      </div>
      {hint ? <span className="hint">{hint}</span> : null}
    </label>
  );
}

/** Kept as a named export: several pages import it for their own errors. */
export function ErrorBox({ children }: { children: React.ReactNode }) {
  return <Notice tone="danger">{children}</Notice>;
}
